import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AppSetting {
  id: string;
  key: string;
  value: any;
  updated_at: string;
}

interface StagingModeValue {
  enabled: boolean;
}

export const useAppSettings = (key: string) => {
  return useQuery({
    queryKey: ['app-settings', key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .eq('key', key)
        .maybeSingle();

      if (error) throw error;
      return data as AppSetting | null;
    },
  });
};

export const useStagingMode = () => {
  const { data: setting, isLoading } = useAppSettings('staging_mode');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updateStagingMode = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const value = { enabled };
      
      const { error } = await supabase
        .from('app_settings')
        .update({ 
          value: value as any,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('key', 'staging_mode');

      if (error) throw error;
    },
    onSuccess: (_, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['app-settings', 'staging_mode'] });
      toast({
        title: enabled ? '🚧 Staging Mode Enabled' : '✅ Production Mode',
        description: enabled 
          ? 'Non-admin users are now blocked from accessing the app.'
          : 'All users can now access the app normally.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update staging mode',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const isEnabled = (setting?.value as StagingModeValue)?.enabled ?? false;

  return {
    isEnabled,
    isLoading,
    updateStagingMode: updateStagingMode.mutate,
    isUpdating: updateStagingMode.isPending,
  };
};